import { useMemo, useState, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ImageOff, Pen, Pencil, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  DESIGN_CARD_MEDIA_ENTITY_TYPE,
  type DesignCard,
  type DesignCardStatus,
  type OrderWithItems,
} from '@tailoring-crm/shared';
import { useApiClient } from '../../lib/api/api-context.js';
import { formatDate } from '../../lib/format.js';
import { listMedia } from '../../lib/media/media-api.js';
import { Badge, type BadgeProps, Button } from '@tailoring-crm/ui';
import { MediaLightbox } from '../media/media-lightbox.js';

/**
 * One card in the board's grid.
 *
 * Its images are read with the generic `listMedia` for `entityType=design_card,
 * entityId=<card id>` — never through the card's own `mediaIds`, which is the ordered contract
 * a future task can also point at gallery-picked images, not what this screen renders from.
 */

const STATUS_VARIANT: Record<DesignCardStatus, NonNullable<BadgeProps['variant']>> = {
  idea: 'secondary',
  shared: 'default',
  approved: 'success',
  rejected: 'destructive',
};

export interface DesignCardTileProps {
  card: DesignCard;
  /** The order behind an order board, for the garment label. A person's board has none. */
  order: OrderWithItems | null;
  /** Both absent on a read-only board — one shown beside other work. */
  onEdit?: () => void;
  onDelete?: () => void;
  /**
   * Open the DRAWING. Present on a read-only board too, unlike the pencil and the bin, because
   * opening a canvas to look at it is a read — the dialog is what refuses to let it be edited.
   */
  onSketch?: () => void;
}

export function DesignCardTile({
  card,
  order,
  onEdit,
  onDelete,
  onSketch,
}: DesignCardTileProps): ReactElement {
  const { t } = useTranslation();
  const client = useApiClient();
  const [activeMediaId, setActiveMediaId] = useState<string | null>(null);

  const media = useQuery({
    queryKey: ['media', DESIGN_CARD_MEDIA_ENTITY_TYPE, card.id],
    queryFn: () =>
      listMedia(client, { entityType: DESIGN_CARD_MEDIA_ENTITY_TYPE, entityId: card.id }),
  });
  const items = media.data ?? [];
  const preview = items[0];

  /**
   * "Which garment" only means something once there is an order. On a person's board — where the
   * design is agreed before anything is booked — the line below carries the date instead, because
   * "Whole order" on a card that belongs to no order is a phrase that answers nothing.
   */
  const garmentLabel = useMemo(() => {
    if (!card.orderId) return null;
    if (!card.orderItemId) return t('designBoard.wholeOrder');
    return (
      order?.items.find((item) => item.id === card.orderItemId)?.garmentTypeLabel ??
      t('designBoard.wholeOrder')
    );
  }, [card.orderId, card.orderItemId, order, t]);

  return (
    <li
      className="flex flex-col overflow-hidden rounded-lg border border-border"
      data-testid="design-card"
    >
      <button
        type="button"
        className="relative block aspect-[4/3] w-full bg-muted disabled:cursor-default"
        disabled={!preview}
        onClick={() => preview && setActiveMediaId(preview.id)}
        aria-label={t('designBoard.viewImages', { title: card.title })}
      >
        {preview?.thumbnailUrl ? (
          <img
            src={preview.thumbnailUrl}
            alt={card.title}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover"
          />
        ) : (
          <span className="flex h-full w-full items-center justify-center text-muted-foreground">
            <ImageOff aria-hidden="true" className="size-8" />
          </span>
        )}
        {/* A drawn card is worth telling apart from a photographed one at a glance: the thumbnail
            of a sketch is a picture like any other, and the shop is looking for the one it drew. */}
        {card.hasSketch ? (
          <span className="absolute left-1.5 top-1.5 flex items-center gap-1 rounded-full bg-background/85 px-2 py-0.5 text-xs font-medium">
            <Pen aria-hidden="true" className="size-3" />
            {t('designBoard.sketch.badge')}
          </span>
        ) : null}
        {items.length > 1 ? (
          <span className="absolute bottom-1.5 right-1.5 rounded-full bg-background/85 px-2 py-0.5 text-xs font-medium">
            {items.length}
          </span>
        ) : null}
      </button>

      <div className="flex flex-1 flex-col gap-1.5 p-3">
        <div className="flex items-start justify-between gap-2">
          <p className="min-w-0 truncate text-sm font-semibold">{card.title}</p>
          <Badge variant={STATUS_VARIANT[card.status]}>
            {t(`designBoard.status.${card.status}`)}
          </Badge>
        </div>
        <p className="truncate text-xs text-muted-foreground" data-testid="design-card-meta">
          {garmentLabel === null
            ? formatDate(card.createdAt)
            : `${order ? order.orderNumber : ''} · ${garmentLabel}`}
        </p>

        {/* A read-only board draws no pencil and no bin: the API would take the write, but the
            screen showing the card is not the screen that owns it. */}
        {onEdit || onDelete || onSketch ? (
          <div className="mt-auto flex justify-end gap-1 pt-2">
            {onSketch ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={onSketch}
                aria-label={t(
                  onEdit
                    ? card.hasSketch
                      ? 'designBoard.sketch.editAction'
                      : 'designBoard.sketch.drawAction'
                    : 'designBoard.sketch.viewAction',
                  { title: card.title },
                )}
              >
                <Pen aria-hidden="true" />
              </Button>
            ) : null}
            {onEdit ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={onEdit}
                aria-label={t('designBoard.editAction', { title: card.title })}
              >
                <Pencil aria-hidden="true" />
              </Button>
            ) : null}
            {onDelete ? (
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive"
                onClick={onDelete}
                aria-label={t('designBoard.deleteAction', { title: card.title })}
              >
                <Trash2 aria-hidden="true" />
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      <MediaLightbox items={items} activeId={activeMediaId} onActiveIdChange={setActiveMediaId} />
    </li>
  );
}
